/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import isString from 'lodash.isstring';
import {translate} from '@ion/core/src/locale/locale';
import {textSortComparator} from '@ion/core/src/utils/SortUtils';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {InfoIcon} from '@ion/dls/src/icons/InfoIcon';
import SearchFieldDefaultTooltip, {
  SearchFieldDefaultTooltipProps
} from './SearchFieldDefaultTooltip';

/**
 * By default, the `admin-devices:modularsearch.devices.any.fields` are mapped to
 * their respective `admin-devices:grid.columns` keys so as to display the fields
 * which will be searched which have corresponding Columns. In some cases, the
 * `grid.columns` keys do not contain direct one-to-one mappings to `any.fields`,
 * in which case the `columnMappings` are used to provide the required mappings
 * of `any.fields` to `grid.columns`, whereby the `columnMappings` keys map to
 * `grid.columns` keys, and the values map to `any.fields` entries.
 */
const columnMappings = {
  deviceId   : 'id',
  deviceName : 'name',
  mobileId   : 'userDefinedMobileId',
  lmu        : 'esnAssociatedLMU',
  groups     : 'groups.title'
};

/**
 * Defines the `getMessages` return value which includes the information required
 * for display within the `SearchFieldDeviceAnyTooltip`.
 */
type GetMessagesParams = {
  fields: string[];
  length: number;
  search: string;
  exact: string;
  partial: string;
}

/**
 * Provides a utility which manages creating the tooltip messaging text as well as
 * the mappings of ANY fields to their corresponding Grid Columns names for display
 * within the Tooltip. ANY fields which do not map directly to Grid Columns will be
 * determined via the `columnMappings` definitions.
 */
const getMessages = (): GetMessagesParams => {
  const options = {returnObjects: true};
  const results = translate('admin-devices:modularsearch.devices.any', options);
  if (results && !isString(results)) {
    const {fields, messages: {search, exact, partial}} = results;
    const columns = translate('admin-devices:grid.columns', options);
    const items = [];
    Object.keys(columns).forEach((column: string) => {
      if (fields.indexOf(column) > -1 || fields.indexOf(columnMappings[column]) > -1) {
        items.push(columns[column]);
      }
    });
    return {
      fields : items.sort(textSortComparator),
      length : items.length-1,
      search,
      exact,
      partial
    };
  }
};

/**
 * Defines the additional styling required by the `SearchFieldDeviceAnyTooltip`
 * message, whereby each ANY field is to be displayed in italics.
 */
const useStyles = makeStyles(() => ({
  field : {
    fontStyle : 'italic'
  }
}));

/**
 * The `SearchFieldDeviceAnyTooltip` component displays the Tooltip and associated
 * informational messaging in the context of a `Device` ANY Search which includes a
 * list of `_comboField` properties that are searched.
 *
 * For each field that is searched, if there is a corresponding Column definition in
 * the Devices Grid Columns, that field will be displayed based on the columns label.
 *
 * As a fallback, if the required results are not found, this component will render
 * the default `SearchFieldDefaultTooltip` component.
 */
const SearchFieldDeviceAnyTooltip = ({title}: SearchFieldDefaultTooltipProps) => {
  const classes = useStyles();
  const results = getMessages();
  if (results) {
    const {fields, length, search, exact, partial} = results;
    return (
      <Tooltip title={(
        <>
          <p>{title}.<br/>{exact}</p>
          <p>{partial}</p>
          <p>
            <span>{search}</span> {fields.map((field: string, i: number) => (
              <span className={classes.field}>{(
                i < length ? `${field}, ` : field
              )}</span>
            ))}
          </p>
        </>
      )} arrow>
        <span>
          <InfoIcon />
        </span>
      </Tooltip>
    );
  }
  return <SearchFieldDefaultTooltip title={title} />;
};
export default React.memo(SearchFieldDeviceAnyTooltip);
